import { AnalyticsBrowser } from "@segment/analytics-next";
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from "react";

import { config } from "../config/env.config";

/**
 * AS-1832: minimal Segment integration mirroring the v2 screener_frontend
 * pattern. A singleton `AnalyticsBrowser` is loaded once with the env
 * write key; when no key is configured (local dev) a stub is provided so
 * call sites can still invoke `analytics.track(...)` without guarding.
 *
 * Defaults are exposed via context so individual `track(...)` calls can
 * automatically merge in `sessionId`, UTM params, and (eventually)
 * screener/location/company identifiers as we wire them up.
 */

export interface SegmentDefaults {
  /**
   * AI Screener generation that emitted the event. Hard-coded to `"3.0"`
   * here because this provider lives in `interviewer_frontend` (the 3.0
   * candidate UI). Lisa filters dashboards on this so 2.0 vs 3.0 traffic
   * is separable inside the shared Segment source.
   */
  interviewVersion: "3.0";
  sessionId: string | null;
  interviewLocationId: string | null;
  // Attribution properties mirror v2 screener_frontend defaults so
  // warehouse queries can join 3.0 funnel events back to the company /
  // screener / location without version-specific branches. Named to match
  // v2 (`company`, `screener`, `location`) plus `screener_location_id` for
  // the canonical id-based join.
  company: string | null;
  screener: string | null;
  location: string | null;
  screener_location_id: string | null;
  utm: {
    campaign: string | null;
    content: string | null;
    medium: string | null;
    source: string | null;
    term: string | null;
  };
}

interface SegmentContextType {
  analytics: AnalyticsBrowser | null;
  defaults: SegmentDefaults;
}

const noopContext: SegmentContextType = {
  analytics: null,
  defaults: {
    interviewVersion: "3.0",
    sessionId: null,
    interviewLocationId: null,
    company: null,
    screener: null,
    location: null,
    screener_location_id: null,
    utm: {
      campaign: null,
      content: null,
      medium: null,
      source: null,
      term: null,
    },
  },
};

const SegmentContext = createContext<SegmentContextType>(noopContext);

// Module-level singleton — load once at import time, not per-render.
const analyticsSingleton: AnalyticsBrowser | null = config.segmentWriteKey
  ? AnalyticsBrowser.load({ writeKey: config.segmentWriteKey })
  : null;

// Warn once per actual browser session, not at module-evaluation time (which
// runs in tests on every vi.resetModules() reload).
if (!config.segmentWriteKey && typeof window !== "undefined") {
  console.warn(
    "[segment] VITE_SEGMENT_WRITE_KEY not set; analytics calls are no-ops",
  );
}

interface SegmentContextProviderProps {
  interviewLocationId: string | null;
  sessionId: string | null;
  // Attribution props are nullable so the provider can mount immediately
  // (keeping the ChatContainer / useInterview subtree stable) and pick up
  // the values once `useLocationHeaderInfo` resolves. The caller gates the
  // page tracker on attribution readiness, while `Landed on Screener` also
  // waits for a session id. A null companyName in the DB therefore doesn't
  // deadlock the interview page or its analytics.
  company: string | null;
  screener: string | null;
  location: string | null;
  screenerLocationId: string | null;
}

function readUtmFromLocation(): SegmentDefaults["utm"] {
  if (typeof window === "undefined") {
    return {
      campaign: null,
      content: null,
      medium: null,
      source: null,
      term: null,
    };
  }
  const params = new URLSearchParams(window.location.search);
  const read = (key: string) => params.get(key) || null;
  return {
    campaign: read("utm_campaign"),
    content: read("utm_content"),
    medium: read("utm_medium"),
    source: read("utm_source"),
    term: read("utm_term"),
  };
}

export const SegmentContextProvider = ({
  children,
  interviewLocationId,
  sessionId,
  company,
  screener,
  location,
  screenerLocationId,
}: PropsWithChildren<SegmentContextProviderProps>) => {
  // UTM params come from the entry URL and don't change during a session,
  // so capture once. `useMemo` rather than `useState` because we never
  // need to update them after first render.
  const utm = useMemo(() => readUtmFromLocation(), []);

  const value = useMemo<SegmentContextType>(
    () => ({
      analytics: analyticsSingleton,
      defaults: {
        interviewVersion: "3.0",
        sessionId,
        interviewLocationId,
        company,
        screener,
        location,
        screener_location_id: screenerLocationId,
        utm,
      },
    }),
    [
      interviewLocationId,
      sessionId,
      company,
      screener,
      location,
      screenerLocationId,
      utm,
    ],
  );

  return (
    <SegmentContext.Provider value={value}>{children}</SegmentContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSegmentContext = (): SegmentContextType =>
  useContext(SegmentContext);
